import React, { useEffect, useMemo, useRef, useState } from "react";
import { meApi, api, formatApiError } from "@/lib/api";
import { PageHeader, Card, TextArea, Field, SaveBar } from "./_shared";
import {
  Search,
  Plus,
  Trash2,
  ExternalLink,
  Sparkles,
  Heart,
  DoorOpen,
  Ban,
  Music2,
  Cake,
  Loader2,
  Check,
  Play,
  X,
  Lock,
} from "lucide-react";

const CATEGORIES = [
  { key: "entree", label: "Entrée des mariés", icon: DoorOpen, color: "text-sky-300" },
  { key: "vin_honneur", label: "Vin d'honneur", icon: Sparkles, color: "text-amber-200" },
  { key: "cocktail", label: "Cocktail", icon: Sparkles, color: "text-emerald-300" },
  { key: "ouverture_bal", label: "Ouverture de bal", icon: Heart, color: "text-rose-300" },
  { key: "soiree", label: "Soirée", icon: Sparkles, color: "text-[#DCB457]" },
  { key: "dessert", label: "Dessert", icon: Cake, color: "text-amber-300" },
  { key: "premiere_danse", label: "Première danse", icon: Music2, color: "text-fuchsia-300" },
  { key: "do_not_play", label: "À ne pas passer", icon: Ban, color: "text-red-300" },
];

const DEFAULT_CATEGORY = "soiree";

function fmtDur(ms) {
  if (!ms) return "—";
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export default function Playlist() {
  const [data, setData] = useState({
    must_play: [],
    do_not_play: [],
    first_dance: "",
    entrance_song: "",
    notes: "",
    spotify_tracks: [],
  });
  const [lock, setLock] = useState({ is_locked: false, days_until_event: null, lock_deadline: null, event_date: null, lock_days_before: 15 });
  const [must_text, setMustText] = useState("");
  const [donot_text, setDonotText] = useState("");

  // Search
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchErr, setSearchErr] = useState("");

  // Save state
  const [autoSaveStatus, setAutoSaveStatus] = useState(""); // "saving" | "saved" | ""
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const initialLoaded = useRef(false);
  const saveTimer = useRef(null);
  const searchTimer = useRef(null);

  // Initial load
  useEffect(() => {
    meApi
      .getPlaylist()
      .then((d) => {
        setData((prev) => ({ ...prev, ...d, spotify_tracks: d.spotify_tracks || [] }));
        setMustText((d.must_play || []).join("\n"));
        setDonotText((d.do_not_play || []).join("\n"));
        if (d.lock) setLock(d.lock);
        initialLoaded.current = true;
      })
      .catch(() => {
        initialLoaded.current = true;
      });
  }, []);

  // ====== Auto-save ======
  const triggerAutoSave = (next) => {
    if (!initialLoaded.current) return;
    if (lock.is_locked) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setAutoSaveStatus("saving");
    saveTimer.current = setTimeout(async () => {
      try {
        const payload = {
          must_play: must_text.split(/\r?\n/).map((s) => s.trim()).filter(Boolean),
          do_not_play: donot_text.split(/\r?\n/).map((s) => s.trim()).filter(Boolean),
          first_dance: data.first_dance || "",
          entrance_song: data.entrance_song || "",
          notes: data.notes || "",
          spotify_tracks: (next && next.spotify_tracks) || data.spotify_tracks || [],
          ...(next || {}),
        };
        await meApi.savePlaylist(payload);
        setAutoSaveStatus("saved");
        setTimeout(() => setAutoSaveStatus((s) => (s === "saved" ? "" : s)), 2500);
      } catch (e) {
        setAutoSaveStatus("");
        setErr(formatApiError(e));
      }
    }, 1500);
  };

  // Trigger autosave on data changes (after initial load)
  useEffect(() => {
    if (!initialLoaded.current) return;
    triggerAutoSave();
    // eslint-disable-next-line
  }, [data.first_dance, data.entrance_song, data.notes, must_text, donot_text, data.spotify_tracks]);

  // ====== Spotify search (debounced) ======
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!q.trim()) {
      setResults([]);
      setSearchErr("");
      return;
    }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      setSearchErr("");
      try {
        const r = await api.get("/me/spotify/search", { params: { q, limit: 10 } });
        setResults(r.data.tracks || []);
      } catch (e) {
        setSearchErr(formatApiError(e));
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => searchTimer.current && clearTimeout(searchTimer.current);
  }, [q]);

  // ====== Track add / move / remove ======
  const [playingId, setPlayingId] = useState(null);

  const togglePlay = (id) => setPlayingId((p) => (p === id ? null : id));

  const addTrack = (track, category = DEFAULT_CATEGORY) => {
    setData((prev) => {
      const exists = (prev.spotify_tracks || []).some((t) => t.id === track.id);
      const next = exists
        ? prev.spotify_tracks.map((t) => (t.id === track.id ? { ...t, category } : t))
        : [...(prev.spotify_tracks || []), { ...track, category, note: "" }];
      return { ...prev, spotify_tracks: next };
    });
  };
  const removeTrack = (id) => {
    setData((prev) => ({ ...prev, spotify_tracks: (prev.spotify_tracks || []).filter((t) => t.id !== id) }));
  };
  const setTrackCategory = (id, category) => {
    setData((prev) => ({
      ...prev,
      spotify_tracks: (prev.spotify_tracks || []).map((t) => (t.id === id ? { ...t, category } : t)),
    }));
  };
  const setTrackNote = (id, note) => {
    setData((prev) => ({
      ...prev,
      spotify_tracks: (prev.spotify_tracks || []).map((t) => (t.id === id ? { ...t, note } : t)),
    }));
  };

  const trackIds = useMemo(() => new Set((data.spotify_tracks || []).map((t) => t.id)), [data.spotify_tracks]);

  // Group by category (with migration of legacy categories on display)
  const LEGACY_MAP = { must_play: "soiree", first_dance: "premiere_danse", entrance: "entree" };
  const grouped = useMemo(() => {
    const out = {};
    CATEGORIES.forEach((c) => { out[c.key] = []; });
    (data.spotify_tracks || []).forEach((t) => {
      const cat = LEGACY_MAP[t.category] || (out[t.category] ? t.category : DEFAULT_CATEGORY);
      out[cat].push(t);
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.spotify_tracks]);

  // ====== Manual save (still available) ======
  const submitForm = async (e) => {
    e.preventDefault();
    setMsg(""); setErr(""); setSaving(true);
    try {
      await meApi.savePlaylist({
        must_play: must_text.split(/\r?\n/).map((s) => s.trim()).filter(Boolean),
        do_not_play: donot_text.split(/\r?\n/).map((s) => s.trim()).filter(Boolean),
        first_dance: data.first_dance || "",
        entrance_song: data.entrance_song || "",
        notes: data.notes || "",
        spotify_tracks: data.spotify_tracks || [],
      });
      setMsg("Playlist enregistrée.");
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="ec-playlist">
      <PageHeader
        eyebrow="Animation"
        title="Ma"
        italic="Playlist."
        desc="Recherchez vos titres directement sur Spotify et composez votre soirée. Tout est sauvegardé automatiquement."
      />

      {/* Playlist lock banner (J-15 before event) */}
      {lock.is_locked && (
        <div
          data-testid="playlist-lock-banner"
          className="mb-6 border border-[#DCB457]/60 bg-[#DCB457]/10 p-5 md:p-6 flex items-start gap-4"
        >
          <Lock size={22} strokeWidth={1.4} className="text-[#DCB457] shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-serif-luxe text-lg md:text-xl leading-tight">
              Playlist verrouillée{lock.days_until_event != null ? <> — J-{Math.max(lock.days_until_event, 0)} avant votre événement</> : null}
            </p>
            <p className="text-white/70 text-sm mt-2 leading-relaxed">
              À {lock.lock_days_before} jours de l'événement, votre playlist est figée pour permettre à notre DJ de préparer votre soirée sereinement.
              {lock.event_date && <> Votre date : <strong className="text-[#DCB457]">{new Date(lock.event_date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</strong>.</>}
              <br />Pour toute demande de dernière minute, contactez-nous par email ou téléphone.
            </p>
          </div>
        </div>
      )}

      {/* Playlist countdown before lock (J-30 → J-15) */}
      {!lock.is_locked && lock.days_until_event != null && lock.days_until_event <= 30 && lock.days_until_event >= 0 && (
        <div
          data-testid="playlist-lock-countdown"
          className="mb-6 border border-amber-400/40 bg-amber-500/5 p-4 md:p-5 flex items-start gap-3"
        >
          <Lock size={18} strokeWidth={1.4} className="text-amber-300 shrink-0 mt-0.5" />
          <p className="text-sm text-white/75 leading-relaxed">
            Verrouillage automatique de votre playlist le{" "}
            <strong className="text-[#DCB457]">
              {new Date(lock.lock_deadline).toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}
            </strong>{" "}
            (J-{lock.lock_days_before} avant votre événement). Il vous reste{" "}
            <strong className="text-[#DCB457]">{Math.max(lock.days_until_event - lock.lock_days_before, 0)} jour{Math.max(lock.days_until_event - lock.lock_days_before, 0) > 1 ? "s" : ""}</strong>{" "}
            pour finaliser.
          </p>
        </div>
      )}

      {/* Auto-save indicator */}
      <div className="flex items-center gap-2 mb-4 text-[11px] uppercase tracking-[0.22em] text-white/45 min-h-[16px]" data-testid="autosave-status">
        {autoSaveStatus === "saving" && (<><Loader2 size={12} className="animate-spin text-[#DCB457]" /> Sauvegarde…</>)}
        {autoSaveStatus === "saved" && (<><Check size={12} className="text-emerald-400" /> <span className="text-emerald-300/90">Enregistré automatiquement</span></>)}
      </div>

      {/* ====== Spotify search ====== */}
      <Card className={`mb-6 ${lock.is_locked ? "opacity-50 pointer-events-none select-none" : ""}`}>
        <div className="flex items-center gap-3 mb-5">
          <Music2 size={20} strokeWidth={1.2} className="text-[#1DB954]" />
          <h2 className="font-serif-luxe text-xl">Recherche Spotify</h2>
        </div>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" strokeWidth={1.4} />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Recherchez un titre, un artiste, un album…"
            data-testid="spotify-search-input"
            className="w-full bg-black/60 border border-[#DCB457]/25 pl-10 pr-10 py-3 text-sm text-white focus:border-[#DCB457] focus:outline-none transition"
          />
          {searching && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#DCB457] animate-spin" />}
        </div>
        {searchErr && <p className="text-xs text-red-300 mt-3">{searchErr}</p>}

        {results.length > 0 && (
          <ul className="mt-5 divide-y divide-[#DCB457]/10 max-h-[460px] overflow-y-auto" data-testid="spotify-results">
            {results.map((t) => {
              const already = trackIds.has(t.id);
              const isPlaying = playingId === `search-${t.id}`;
              return (
                <li key={t.id} data-testid={`search-row-${t.id}`}>
                  <div className="py-3 flex items-center gap-4">
                    {t.album_image ? (
                      <img src={t.album_image} alt={`Pochette ${t.name || "titre"}${t.artists ? ' — ' + t.artists : ''}`} loading="lazy" decoding="async" width="56" height="56" className="w-14 h-14 object-cover shrink-0" />
                    ) : (
                      <div className="w-14 h-14 bg-black/40 shrink-0 flex items-center justify-center text-white/30"><Music2 size={20} /></div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate" title={t.name}>{t.name}</p>
                      <p className="text-xs text-white/55 truncate">{t.artists} · {t.album_name}</p>
                      <p className="text-[10px] uppercase tracking-[0.18em] text-white/35 mt-1">{fmtDur(t.duration_ms)}{t.explicit && " · Explicit"}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => togglePlay(`search-${t.id}`)}
                      data-testid={`preview-${t.id}`}
                      title={isPlaying ? "Masquer le player" : "Pré-écouter"}
                      className={`shrink-0 w-9 h-9 inline-flex items-center justify-center border transition ${isPlaying ? "bg-[#1DB954] border-[#1DB954] text-black" : "border-[#1DB954]/50 text-[#1DB954] hover:bg-[#1DB954]/10"}`}
                    >
                      {isPlaying ? <X size={14} strokeWidth={1.6} /> : <Play size={14} strokeWidth={1.6} className="ml-0.5" />}
                    </button>
                    <a href={t.external_url} target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-[#1DB954] transition shrink-0" title="Ouvrir sur Spotify">
                      <ExternalLink size={15} strokeWidth={1.4} />
                    </a>
                    {already ? (
                      <span className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 border border-emerald-400/40 px-2 py-1.5 inline-flex items-center gap-1 shrink-0">
                        <Check size={12} strokeWidth={1.6} /> Ajoutée
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => addTrack(t)}
                        data-testid={`add-track-${t.id}`}
                        className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-black bg-[#DCB457] px-3 py-1.5 hover:bg-[#e6c454] transition shrink-0"
                      >
                        <Plus size={12} strokeWidth={1.6} /> Ajouter
                      </button>
                    )}
                  </div>
                  {isPlaying && (
                    <div className="pb-3" data-testid={`embed-search-${t.id}`}>
                      <iframe
                        src={`https://open.spotify.com/embed/track/${t.id}`}
                        width="100%"
                        height="80"
                        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                        loading="lazy"
                        title={`Spotify preview ${t.name}`}
                        style={{ border: 0 }}
                      />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {q && !searching && results.length === 0 && !searchErr && (
          <p className="text-sm text-white/45 italic mt-4">Aucun résultat pour « {q} ».</p>
        )}
      </Card>

      {/* ====== Selected tracks by category ====== */}
      <Card className={`mb-6 ${lock.is_locked ? "opacity-70 pointer-events-none select-none" : ""}`}>
        <div className="flex items-baseline justify-between mb-5 gap-3 flex-wrap">
          <h2 className="font-serif-luxe text-xl">Mes titres sélectionnés</h2>
          <span className="text-[11px] uppercase tracking-[0.22em] text-white/45" data-testid="tracks-count">
            {(data.spotify_tracks || []).length} titre{(data.spotify_tracks || []).length > 1 ? "s" : ""}
          </span>
        </div>

        {(data.spotify_tracks || []).length === 0 ? (
          <p className="text-sm text-white/45 italic">
            Aucun titre encore ajouté. Utilisez la recherche ci-dessus pour commencer à composer votre playlist.
          </p>
        ) : (
          <div className="space-y-7">
            {CATEGORIES.map(({ key, label, icon: Icon, color }) => {
              const tracks = grouped[key] || [];
              if (tracks.length === 0) return null;
              return (
                <div key={key} data-testid={`group-${key}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon size={15} strokeWidth={1.3} className={color} />
                    <h3 className="text-[11px] uppercase tracking-[0.22em] text-white/70">{label} <span className="text-white/35">· {tracks.length}</span></h3>
                  </div>
                  <ul className="divide-y divide-[#DCB457]/10">
                    {tracks.map((t) => {
                      const isPlaying = playingId === `sel-${t.id}`;
                      return (
                        <li key={t.id} data-testid={`track-${t.id}`}>
                          <div className="py-3 flex items-center gap-3 flex-wrap">
                            {t.album_image ? (
                              <img src={t.album_image} alt={`Pochette ${t.name || "titre"}${t.artists ? ' — ' + t.artists : ''}`} loading="lazy" decoding="async" width="48" height="48" className="w-12 h-12 object-cover shrink-0" />
                            ) : (
                              <div className="w-12 h-12 bg-black/40 shrink-0 flex items-center justify-center text-white/30"><Music2 size={18} /></div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white truncate">{t.name}</p>
                              <p className="text-xs text-white/55 truncate">{t.artists}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => togglePlay(`sel-${t.id}`)}
                              data-testid={`sel-preview-${t.id}`}
                              title={isPlaying ? "Masquer le player" : "Pré-écouter"}
                              className={`shrink-0 w-8 h-8 inline-flex items-center justify-center border transition ${isPlaying ? "bg-[#1DB954] border-[#1DB954] text-black" : "border-[#1DB954]/50 text-[#1DB954] hover:bg-[#1DB954]/10"}`}
                            >
                              {isPlaying ? <X size={13} strokeWidth={1.6} /> : <Play size={13} strokeWidth={1.6} className="ml-0.5" />}
                            </button>
                            <select
                              value={CATEGORIES.find((c) => c.key === t.category) ? t.category : DEFAULT_CATEGORY}
                              onChange={(e) => setTrackCategory(t.id, e.target.value)}
                              data-testid={`track-cat-${t.id}`}
                              className="bg-black/60 border border-[#DCB457]/25 px-2 py-1.5 text-xs text-white focus:border-[#DCB457] outline-none"
                            >
                              {CATEGORIES.map((c) => (<option key={c.key} value={c.key} className="bg-black">{c.label}</option>))}
                            </select>
                            <input
                              type="text"
                              value={t.note || ""}
                              onChange={(e) => setTrackNote(t.id, e.target.value)}
                              placeholder="Note (optionnel)"
                              data-testid={`track-note-${t.id}`}
                              className="bg-black/60 border border-[#DCB457]/25 px-2 py-1.5 text-xs text-white focus:border-[#DCB457] outline-none w-32"
                            />
                            <a href={t.external_url} target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-[#1DB954] transition shrink-0" title="Ouvrir sur Spotify">
                              <ExternalLink size={14} strokeWidth={1.4} />
                            </a>
                            <button type="button" onClick={() => removeTrack(t.id)} data-testid={`rm-track-${t.id}`} className="text-red-300 hover:text-red-400 shrink-0 border border-red-400/30 hover:border-red-400/60 px-2 py-1.5">
                              <Trash2 size={13} strokeWidth={1.4} />
                            </button>
                          </div>
                          {isPlaying && (
                            <div className="pb-3" data-testid={`embed-sel-${t.id}`}>
                              <iframe
                                src={`https://open.spotify.com/embed/track/${t.id}`}
                                width="100%"
                                height="80"
                                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                                loading="lazy"
                                title={`Spotify preview ${t.name}`}
                                style={{ border: 0 }}
                              />
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* ====== Free text fallback (songs not on Spotify, ambiance notes, etc.) ====== */}
      <Card>
        <h2 className="font-serif-luxe text-xl mb-1">Notes libres</h2>
        <p className="text-xs text-white/55 mb-5">
          Pour les titres absents de Spotify, les souvenirs personnels, l'ambiance, etc.
        </p>
        <form onSubmit={submitForm} className="space-y-5">
          <fieldset disabled={lock.is_locked} className={lock.is_locked ? "opacity-60" : ""}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Musique d'entrée (texte libre)" value={data.entrance_song} onChange={(v) => setData({ ...data, entrance_song: v })} testid="pl-entrance" placeholder="Artiste — Titre" />
              <Field label="Première danse (texte libre)" value={data.first_dance} onChange={(v) => setData({ ...data, first_dance: v })} testid="pl-firstdance" placeholder="Artiste — Titre" />
            </div>
            <TextArea label="Titres complémentaires hors Spotify (un par ligne)" rows={4} testid="pl-mustplay" value={must_text} onChange={(v) => setMustText(v)} placeholder={"Artiste — Titre\nAutre artiste — Autre titre"} />
            <TextArea label="Titres à ne PAS passer (un par ligne)" rows={3} testid="pl-donotplay" value={donot_text} onChange={(v) => setDonotText(v)} placeholder="Aucune chanson de…" />
            <TextArea label="Notes & ambiance souhaitée" rows={3} testid="pl-notes" value={data.notes} onChange={(v) => setData({ ...data, notes: v })} />
            {err && <p className="text-xs text-red-300">{err}</p>}
            {!lock.is_locked && <SaveBar saving={saving} message={msg || (autoSaveStatus === "saved" ? "Tout est enregistré." : "")} />}
          </fieldset>
          <div className="flex flex-wrap gap-2 pt-4 border-t border-[#DCB457]/10 mt-4">
            <button
              type="button"
              onClick={async () => {
                const token = localStorage.getItem("ma_token");
                const r = await fetch(`${api.defaults.baseURL}/me/playlist/pdf`, { headers: { Authorization: `Bearer ${token}` } });
                const blob = await r.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = "playlist-my-animation.pdf"; a.click();
                setTimeout(() => URL.revokeObjectURL(url), 5000);
              }}
              data-testid="pl-export-pdf"
              className="text-[11px] uppercase tracking-[0.18em] text-[#DCB457] border border-[#DCB457]/40 px-3 py-2 hover:bg-[#DCB457]/10 inline-flex items-center gap-1.5"
            >
              📄 PDF imprimable
            </button>
            <button
              type="button"
              onClick={async () => {
                const token = localStorage.getItem("ma_token");
                try {
                  const r = await fetch(`${api.defaults.baseURL}/me/playlist/spotify`, { headers: { Authorization: `Bearer ${token}` } });
                  if (!r.ok) throw new Error("Erreur");
                  const html = await r.text();
                  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
                  const url = URL.createObjectURL(blob);
                  window.open(url, "_blank", "noopener,noreferrer");
                  setTimeout(() => URL.revokeObjectURL(url), 60000);
                } catch {
                  // fallback: just open Spotify search homepage
                  window.open("https://open.spotify.com", "_blank", "noopener,noreferrer");
                }
              }}
              data-testid="pl-export-spotify"
              className="text-[11px] uppercase tracking-[0.18em] text-[#1DB954] border border-[#1DB954]/40 px-3 py-2 hover:bg-[#1DB954]/10 inline-flex items-center gap-1.5"
            >
              🎵 Ouvrir dans Spotify
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
}
